/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaRoute, FaCheck, FaChevronLeft } from 'react-icons/fa';

import Modal from '../../../../../components/Modal/Modal';
import { GhostButton, PrimaryButton } from './styles';

// ===========================================================================
// ConfirmItemModal — resumo do item antes do POST. O "Cadastrar item" valida e
// abre este modal; o POST só dispara no "Confirmar cadastro". Componente burro:
// recebe o `summary` já montado pelo pai.
// ===========================================================================

const ModeBadge = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   margin-bottom: 4px;
   padding: 4px 10px;
   border-radius: 999px;
   font-size: 11.5px;
   font-weight: 600;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   background: ${({ theme }) => theme?.colors?.surfaceMuted ?? '#eef1f5'};

   .dotc {
      width: 6px;
      height: 6px;
      border-radius: 999px;
      background: ${({ $inline }) => ($inline ? '#e8843c' : '#3fae6f')};
   }
`;

const SumList = styled.div`
   display: flex;
   flex-direction: column;
`;

const Row = styled.div`
   display: flex;
   gap: 11px;
   padding: 13px 0;
   border-bottom: 1px solid ${({ theme }) => theme?.colors?.borderLight ?? 'rgba(0,0,0,0.06)'};

   &:last-child {
      border-bottom: none;
   }

   .dot {
      flex-shrink: 0;
      width: 9px;
      height: 9px;
      margin-top: 4px;
      border-radius: 999px;
      border: 2px solid #d3d8df;
      background: #fff;
   }
   &.filled .dot {
      background: ${({ $warm, theme }) =>
         ($warm ? '#e8843c' : theme?.colors?.primary ?? '#185FA5')};
      border-color: ${({ $warm, theme }) =>
         ($warm ? '#e8843c' : theme?.colors?.primary ?? '#185FA5')};
   }
   .main {
      flex: 1;
      min-width: 0;
   }
   .k {
      font-size: 10.5px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      font-weight: 700;
      color: #9aa1ab;
   }
   .v {
      margin-top: 2px;
      font-size: 13.5px;
      font-weight: 600;
      line-height: 1.4;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
      word-break: break-word;
   }
   .v.muted {
      font-weight: 500;
      font-style: italic;
      color: #b3b9c2;
   }
   .v .mono {
      font-variant-numeric: tabular-nums;
   }
`;

const Chips = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 6px;
   margin-top: 4px;

   .chip {
      padding: 3px 9px;
      border-radius: 999px;
      font-size: 11.5px;
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
      background: ${({ theme }) => theme?.colors?.surfaceMuted ?? '#f0f2f5'};
   }
   .chip.acc {
      color: ${({ theme }) => theme?.colors?.primary ?? '#185FA5'};
      background: ${({ theme }) => theme?.colors?.primarySoft ?? 'rgba(24,95,165,0.12)'};
   }
   .chip.warm {
      color: #a85e29;
      background: rgba(232, 132, 60, 0.14);
   }
`;

const Title = styled.div`
   display: flex;
   align-items: center;
   gap: 9px;
   font-size: 15px;
   font-weight: 700;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

export default function ConfirmItemModal({
   isOpen,
   onClose,
   onConfirm,
   isSubmitting = false,
   summary,
}) {
   if (!summary) return null;
   const inline = summary.mode === 'INLINE';
   const hasWindow = !!(summary.windowStart && summary.windowEnd);

   const footer = (
      <>
         <GhostButton type="button" onClick={onClose} disabled={isSubmitting}>
            <FaChevronLeft size={13} /> Voltar e corrigir
         </GhostButton>
         <PrimaryButton
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
            style={{ flex: 1 }}
         >
            <FaCheck size={15} />
            {isSubmitting ? 'Cadastrando…' : 'Confirmar cadastro'}
         </PrimaryButton>
      </>
   );

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title="Resumo do item"
         maxWidth="480px"
         footer={footer}
      >
         <ModeBadge $inline={inline}>
            <span className="dotc" />
            {inline ? 'Modo manual (avulso)' : 'Modo vínculo (cadastro)'}
         </ModeBadge>

         <Title>
            <FaRoute size={16} /> Confira antes de cadastrar
         </Title>

         <SumList style={{ marginTop: 6 }}>
            <Row className={summary.customerName ? 'filled' : ''}>
               <span className="dot" />
               <div className="main">
                  <div className="k">Cliente</div>
                  {summary.customerName ? (
                     <div className="v">
                        {summary.customerName}
                        {summary.customerDoc ? (
                           <div
                              style={{
                                 fontSize: 11.5,
                                 fontWeight: 500,
                                 color: '#9aa1ab',
                              }}
                           >
                              {summary.customerDoc}
                           </div>
                        ) : null}
                     </div>
                  ) : (
                     <div className="v muted">Selecione o cliente</div>
                  )}
               </div>
            </Row>

            <Row className={summary.addressText ? 'filled' : ''}>
               <span className="dot" />
               <div className="main">
                  <div className="k">Endereço de serviço</div>
                  <div className={`v${summary.addressText ? '' : ' muted'}`}>
                     {summary.addressText ||
                        (inline ? 'Opcional' : 'Escolha um endereço')}
                  </div>
               </div>
            </Row>

            <Row className="filled">
               <span className="dot" />
               <div className="main">
                  <div className="k">Agendamento</div>
                  <Chips>
                     {summary.dateBR && (
                        <span className="chip acc">{summary.dateBR}</span>
                     )}
                     <span className="chip">{summary.periodLabel}</span>
                     <span className="chip">{summary.baseLabel}</span>
                  </Chips>
               </div>
            </Row>

            <Row className={hasWindow ? 'filled' : ''} $warm>
               <span className="dot" />
               <div className="main">
                  <div className="k">Horário marcado</div>
                  {hasWindow ? (
                     <Chips>
                        <span className="chip warm">
                           {summary.windowStart} – {summary.windowEnd}
                        </span>
                     </Chips>
                  ) : (
                     <div className="v muted">Sem janela definida</div>
                  )}
               </div>
            </Row>

            <Row className={summary.orderNumber ? 'filled' : ''}>
               <span className="dot" />
               <div className="main">
                  <div className="k">Pedido</div>
                  <div className={`v${summary.orderNumber ? '' : ' muted'}`}>
                     {summary.orderNumber ? (
                        <span className="mono">Nº {summary.orderNumber}</span>
                     ) : (
                        'Informe o nº do pedido'
                     )}
                  </div>
               </div>
            </Row>

            <Row className={summary.servicesCount > 0 ? 'filled' : ''}>
               <span className="dot" />
               <div className="main">
                  <div className="k">Serviços e duração</div>
                  {summary.servicesCount > 0 ? (
                     <div className="v">
                        {summary.servicesCount}{' '}
                        {summary.servicesCount === 1 ? 'serviço' : 'serviços'} ·{' '}
                        <span className="mono">{summary.durationLabel}</span>{' '}
                        estimados
                     </div>
                  ) : (
                     <div className="v muted">Pode definir depois</div>
                  )}
               </div>
            </Row>

            {summary.orderValueFormatted ? (
               <Row className="filled">
                  <span className="dot" />
                  <div className="main">
                     <div className="k">Valor do pedido</div>
                     <div className="v">
                        <span className="mono">{summary.orderValueFormatted}</span>
                     </div>
                  </div>
               </Row>
            ) : null}
         </SumList>
      </Modal>
   );
}

ConfirmItemModal.propTypes = {
   isOpen: PropTypes.bool,
   onClose: PropTypes.func,
   onConfirm: PropTypes.func,
   isSubmitting: PropTypes.bool,
   // eslint-disable-next-line react/forbid-prop-types
   summary: PropTypes.object,
};
